import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PatientCameraModal.css';

/**
 * Enterprise LIMS Patient Camera Capture Modal
 * Captures live patient photos using device webcam with framing guides,
 * multi-camera switching, preview confirmation, and automatic MediaStream cleanup.
 */
export const PatientCameraModal = ({
  isOpen,
  onClose,
  onPhotoCaptured,
  onSwitchToUpload,
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const isStartingRef = useRef(false);

  // States
  const [isLoading, setIsLoading] = useState(true);
  const [cameraError, setCameraError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);

  // Helper: Stop all active tracks on the active camera stream
  const stopCameraStream = useCallback(() => {
    if (streamRef.current) {
      try {
        const tracks = streamRef.current.getTracks();
        tracks.forEach((track) => {
          track.stop();
        });
      } catch (err) {
        console.error('Error stopping stream tracks:', err);
      }
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Discover available video devices
  const discoverDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = allDevices.filter((d) => d.kind === 'videoinput');
      setDevices(videoInputs);
    } catch (err) {
      console.warn('Unable to enumerate video devices:', err);
    }
  }, []);

  // Start webcam stream with resilient fallbacks
  const startCamera = useCallback(async (targetDeviceId = null) => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;

    setIsLoading(true);
    setCameraError(null);
    setCapturedImage(null);
    setCapturedBlob(null);

    // Stop existing stream if any
    stopCameraStream();

    // Check browser mediaDevices support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setIsLoading(false);
      isStartingRef.current = false;
      setCameraError({
        type: 'unsupported',
        title: 'Camera Not Supported',
        message: 'Your browser or environment does not support camera capture. Please use the Upload Photo option.',
      });
      return;
    }

    // Brief cooldown to allow Windows / UVC webcam hardware lock release
    await new Promise((resolve) => setTimeout(resolve, 80));

    const deviceIdToUse = targetDeviceId || selectedDeviceId;
    let mediaStream = null;
    let capturedError = null;

    // 1st Attempt: Preferred resolution / selected device (always using ideal, never exact)
    try {
      const constraints = {
        video: deviceIdToUse
          ? { deviceId: { ideal: deviceIdToUse } }
          : {
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
        audio: false,
      };
      mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      console.warn('Preferred camera constraints attempt failed:', err);
      capturedError = err;

      // If user explicitly denied permission, do not retry
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setIsLoading(false);
        isStartingRef.current = false;
        setCameraError({
          type: 'denied',
          title: 'Camera Access Denied',
          message: 'Camera permission was not granted. Please allow camera access in your browser settings or upload a photo instead.',
          details: err.message || err.name,
        });
        return;
      }
    }

    // 2nd Attempt: Generic basic fallback { video: true }
    if (!mediaStream) {
      try {
        console.info('Attempting basic fallback { video: true }...');
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        capturedError = null;
      } catch (fallbackErr) {
        console.error('Basic video fallback also failed:', fallbackErr);
        capturedError = fallbackErr;
      }
    }

    // Handle failure
    if (!mediaStream) {
      setIsLoading(false);
      isStartingRef.current = false;
      const err = capturedError || new Error('Webcam initialization failed');

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError({
          type: 'denied',
          title: 'Camera Access Denied',
          message: 'Camera permission was not granted. Please allow camera access in your browser settings or upload a photo instead.',
          details: err.message || err.name,
        });
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setCameraError({
          type: 'not_found',
          title: 'No Camera Detected',
          message: 'No webcam or camera was found on this workstation. You can upload a photo from file instead.',
          details: err.message || err.name,
        });
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setCameraError({
          type: 'in_use',
          title: 'Camera Unavailable',
          message: 'The webcam is currently in use by another application (e.g. Teams, Zoom, or another browser tab) or disconnected. Please close other camera apps and retry.',
          details: err.message || err.name,
        });
      } else if (err.name === 'OverconstrainedError') {
        setCameraError({
          type: 'overconstrained',
          title: 'Camera Mode Unsupported',
          message: 'The requested camera resolution is not supported by your camera hardware. Please try again or upload a photo.',
          details: err.message || err.name,
        });
      } else {
        setCameraError({
          type: 'general',
          title: 'Camera Error',
          message: 'Unable to initialize webcam. Please verify your camera connection or upload photo from file.',
          details: err.message || err.name,
        });
      }
      return;
    }

    // Success: store active stream
    streamRef.current = mediaStream;

    // Discover devices now that user has granted camera permissions
    await discoverDevices();

    // Set selected device from active track without triggering restart
    const activeTrack = mediaStream.getVideoTracks()[0];
    const activeSettings = activeTrack?.getSettings?.();
    if (activeSettings?.deviceId && !targetDeviceId) {
      setSelectedDeviceId(activeSettings.deviceId);
    }

    if (videoRef.current) {
      videoRef.current.srcObject = mediaStream;
      videoRef.current.onloadedmetadata = () => {
        if (videoRef.current) {
          videoRef.current.play().catch((playErr) => {
            console.warn('Video auto-play interrupted:', playErr);
          });
        }
      };
    }

    setIsLoading(false);
    isStartingRef.current = false;
  }, [discoverDevices, selectedDeviceId, stopCameraStream]);

  // Keep ref for startCamera to avoid re-triggering modal effect on internal state changes
  const startCameraRef = useRef(startCamera);
  useEffect(() => {
    startCameraRef.current = startCamera;
  }, [startCamera]);

  // Start camera on modal open, and cleanup on close
  useEffect(() => {
    if (isOpen) {
      startCameraRef.current();
    } else {
      stopCameraStream();
    }

    return () => {
      stopCameraStream();
    };
  }, [isOpen, stopCameraStream]);

  // Handle ESC key to cancel/close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleCancel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Switch camera device
  const handleDeviceChange = (deviceId) => {
    setSelectedDeviceId(deviceId);
    startCamera(deviceId);
  };

  // Capture still image from video stream
  const handleCapture = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Flip horizontally to match mirror preview
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
        setCapturedBlob(blob);
        setCapturedImage(dataUrl);

        // Pause / stop stream while confirming
        stopCameraStream();
      },
      'image/jpeg',
      0.92
    );
  };

  // Retake photo: clear captured frame and restart camera
  const handleRetake = () => {
    setCapturedImage(null);
    setCapturedBlob(null);
    startCamera();
  };

  // Confirm photo: convert blob to File and dispatch to parent
  const handleUsePhoto = () => {
    if (!capturedBlob || !capturedImage) return;

    const fileName = `patient_photo_${Date.now()}.jpg`;
    const photoFile = new File([capturedBlob], fileName, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });

    stopCameraStream();
    onPhotoCaptured?.(photoFile, capturedImage);
    onClose?.();
  };

  // Cancel / Close modal safely
  const handleCancel = () => {
    stopCameraStream();
    onClose?.();
  };

  // Switch to file upload
  const handleFallbackUpload = () => {
    stopCameraStream();
    onClose?.();
    onSwitchToUpload?.();
  };

  if (!isOpen) return null;

  return (
    <div
      className="patient-camera-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="camera-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div className="patient-camera-modal">
        {/* Header */}
        <div className="patient-camera-header">
          <div className="camera-header-text">
            <h2 id="camera-modal-title" className="camera-modal-title">
              <span>📷</span>
              <span>{capturedImage ? 'Confirm Patient Photo' : 'Capture Patient Photo'}</span>
            </h2>
            <span className="camera-modal-subtitle">
              {capturedImage
                ? 'Review the captured portrait before saving'
                : 'Position patient facing the camera with proper lighting'}
            </span>
          </div>
          <button
            type="button"
            className="camera-close-btn"
            onClick={handleCancel}
            title="Cancel and close camera"
            aria-label="Close camera dialog"
          >
            &times;
          </button>
        </div>

        {/* Viewport / Body */}
        <div className="patient-camera-body">
          {cameraError ? (
            /* Error Card */
            <div className="camera-status-card" role="alert">
              <div className="camera-error-icon">📷⚠️</div>
              <h3 className="camera-error-title">{cameraError.title}</h3>
              <p className="camera-error-msg">{cameraError.message}</p>
              {cameraError.details && (
                <div style={{ fontSize: '0.74rem', color: '#94a3b8', marginBottom: '1rem', fontFamily: 'monospace' }}>
                  Reason: {cameraError.details}
                </div>
              )}
              <div className="camera-error-actions">
                <button
                  type="button"
                  className="camera-btn camera-btn-primary"
                  onClick={handleFallbackUpload}
                >
                  🖼️ Upload Photo Instead
                </button>
                <button
                  type="button"
                  className="camera-btn camera-btn-secondary"
                  onClick={() => startCamera()}
                >
                  🔄 Try Again
                </button>
                <button
                  type="button"
                  className="camera-btn camera-btn-secondary"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Preview Box */}
              <div className="camera-preview-wrapper">
                {isLoading && (
                  <div className="camera-status-card" aria-busy="true">
                    <div className="camera-spinner" />
                    <span className="camera-status-text">Accessing camera stream...</span>
                  </div>
                )}

                {/* Live Stream View */}
                {!capturedImage && (
                  <>
                    <video
                      ref={videoRef}
                      className="camera-video-stream"
                      autoPlay
                      playsInline
                      muted
                      style={{ display: isLoading ? 'none' : 'block' }}
                    />
                    {!isLoading && (
                      <div className="camera-face-guide">
                        <span className="camera-guide-text">Align face inside frame</span>
                      </div>
                    )}
                  </>
                )}

                {/* Captured Still View */}
                {capturedImage && (
                  <img
                    src={capturedImage}
                    alt="Captured Patient Portrait"
                    className="camera-captured-image"
                  />
                )}
              </div>

              {/* Multiple Camera Device Selector */}
              {!capturedImage && devices.length > 1 && (
                <div className="camera-device-bar">
                  <span>Camera:</span>
                  <select
                    className="camera-switch-select"
                    value={selectedDeviceId}
                    onChange={(e) => handleDeviceChange(e.target.value)}
                    aria-label="Switch Camera Device"
                  >
                    {devices.map((device, idx) => (
                      <option key={device.deviceId || idx} value={device.deviceId}>
                        {device.label || `Camera ${idx + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer Controls */}
        {!cameraError && (
          <div className="patient-camera-footer">
            {capturedImage ? (
              /* Review State Actions */
              <>
                <button
                  type="button"
                  className="camera-btn camera-btn-retake"
                  onClick={handleRetake}
                  title="Discard and capture another photo"
                >
                  <span>🔄</span>
                  <span>Retake</span>
                </button>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className="camera-btn camera-btn-secondary"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="camera-btn camera-btn-success"
                    onClick={handleUsePhoto}
                    title="Accept this photo for patient registration"
                  >
                    <span>✓</span>
                    <span>Use Photo</span>
                  </button>
                </div>
              </>
            ) : (
              /* Live Capture Actions */
              <>
                <button
                  type="button"
                  className="camera-btn camera-btn-secondary"
                  onClick={handleFallbackUpload}
                  title="Choose image file from computer"
                >
                  <span>🖼️</span>
                  <span>Upload File Instead</span>
                </button>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className="camera-btn camera-btn-secondary"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="camera-btn camera-btn-primary"
                    onClick={handleCapture}
                    disabled={isLoading}
                    title="Take photo from current camera view"
                    aria-label="Capture Photo"
                  >
                    <span>📷</span>
                    <span>Capture</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientCameraModal;
